import React, {
  PropsWithChildren,
  Children,
  isValidElement,
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useId,
} from 'react';
import isNil from 'lodash-es/isNil';
import classNames from 'classnames';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { motion, LayoutGroup } from 'framer-motion';
import throttle from 'lodash-es/throttle';
import { semantic_colors } from '../../foundation';
import { Stack } from '../stack';

export type TabWidth = 'fill' | 'auto';
export interface LineTabsContextProps {
  width?: TabWidth;
  paddingTop?: boolean;
  activeTabId?: string;
  onChange?: (id: string) => void;
}

export const LineTabContext = createContext<LineTabsContextProps | null>(null);

export const useLineTabContext = () => {
  const context = useContext(LineTabContext);

  if (!context) {
    throw new Error('LineTabContext is not defined');
  }
  return context;
};

export interface LineTabsProps {
  className?: string;
  /**
   * 'fill' 일경우 컨테이너 기준 Full Width 값, 'auto' 일경우 컨텐츠에 따라 자동 너비 계산됩니다.
   * @default 'auto'
   */
  width?: TabWidth;
  /**
   * 좁은 간격 사용 유무
   *  - width가 'auto' 일경우 탭아이템간 column-gap은 항상 0입니다.
   * @default false
   */
  dense?: boolean;
  /**
   * side padding on/off
   * @default true
   */
  paddingHorizontal?: boolean;
  /**
   * @deprecated Use `paddingHorizontal` instead.
   */
  horizontalPadding?: boolean;
  /**
   * 탭 상단 여백 포함 여부
   * @default true
   */
  paddingTop?: boolean;
  activeTabId?: string;
  onChange?: (id: string) => void;
}

export const LineTabs = ({
  className,
  children,
  dense = false,
  paddingHorizontal,
  horizontalPadding,
  paddingTop = true,
  width = 'auto',
  activeTabId,
  onChange,
}: PropsWithChildren<LineTabsProps>) => {
  const id = useId();
  const tabListRef = useRef<HTMLElement | null>(null);
  const scrollWrapperRef = useRef<HTMLDivElement | null>(null);
  const [mask, setMask] = useState({ leftMask: 0, rightMask: 0 });
  const resolvedPaddingHorizontal = !isNil(paddingHorizontal) ? paddingHorizontal : horizontalPadding ?? true;
  const TAB_PADDING = resolvedPaddingHorizontal ? (dense ? 16 : 40) : 0;

  const handleTabScroll = throttle(() => {
    updateMask();
  }, 500);

  const updateMask = () => {
    const scrollContainer = scrollWrapperRef.current;
    if (!scrollContainer) return;

    if (scrollContainer.scrollWidth > (tabListRef.current?.offsetWidth ?? 0)) {
      const showLeftMask = scrollContainer.scrollLeft > 0;
      const showRightMask = scrollContainer.scrollLeft + scrollContainer.offsetWidth < scrollContainer.scrollWidth;

      setMask({
        leftMask: showLeftMask ? 1 : 0,
        rightMask: showRightMask ? 1 : 0,
      });
    }
  };

  const updateActiveScrollPosition = (initial: boolean) => {
    // trigger: first render or mask open
    const scrollMaskOpen = mask.leftMask === 1 || mask.rightMask === 1;

    if (scrollMaskOpen || initial) {
      const scrollContainer = scrollWrapperRef.current;
      const tabListContainer = tabListRef.current;

      if (scrollContainer && tabListContainer) {
        const tabContainerRect = tabListContainer.getBoundingClientRect();
        const filtered_children = Children.toArray(children).filter((child) => !isNil(child));

        Children.forEach(filtered_children, (child, index) => {
          if (!isValidElement(child)) return;
          if (child.props.id === activeTabId) {
            const tab = scrollContainer.children[index];
            const currentTabItemRect = tab.getBoundingClientRect();
            const correction =
              scrollContainer.scrollLeft - tabListContainer.clientWidth / 2 + currentTabItemRect.width / 2;
            const startLeft = initial ? currentTabItemRect.left + TAB_PADDING / 2 : currentTabItemRect.left;

            scrollContainer.scrollTo({
              left: startLeft - tabContainerRect.left + correction,
              behavior: initial ? 'auto' : 'smooth',
            });
          }
        });
      }
    }
  };

  useEffect(() => {
    updateActiveScrollPosition(true);
    updateMask();
  }, []);

  useEffect(() => {
    updateActiveScrollPosition(false);
  }, [activeTabId]);

  return (
    <LineTabContext.Provider value={{ width, paddingTop, activeTabId, onChange }}>
      <TabContainer className={classNames('pds-line-tabs', className)} ref={tabListRef}>
        <Mask style={{ left: 0, opacity: mask.leftMask }} position='left' />
        <Mask style={{ right: 0, opacity: mask.rightMask }} position='right' />
        <IndicatorContainer
          direction='row'
          role='tablist'
          css={[
            css`
              padding: ${`0 ${TAB_PADDING}px`};
              ${width === 'auto' && `column-gap: ${dense ? '16px' : '24px'}`}
            `,
          ]}
          onScroll={handleTabScroll}
          ref={scrollWrapperRef}
        >
          <LayoutGroup id={id}>{children}</LayoutGroup>
        </IndicatorContainer>
      </TabContainer>
    </LineTabContext.Provider>
  );
};

const TabContainer = styled.nav`
  position: relative;
`;

const IndicatorContainer = styled(Stack)`
  position: relative;
  display: flex;
  align-items: center;
  background-color: ${semantic_colors.background.surface};
  box-shadow: inset 0 -1px 0 0 ${semantic_colors.border.primary};
  margin: 0;
  overflow-x: scroll;
  scrollbar-width: none;

  ::-webkit-scrollbar {
    display: none;
  }
`;

const Mask = styled(motion.div)<{ position: 'left' | 'right' }>`
  position: absolute;
  height: 100%;
  top: 0;
  width: 32px;
  pointer-events: none;
  z-index: 1;
  background: linear-gradient(
    ${({ position }) => (position === 'left' ? '90deg' : '270deg')},
    ${semantic_colors.background.surface} 0%,
    rgba(255, 255, 255, 0) 100%
  );
`;
